import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";

const SocieteContext = createContext(null);

export const useSociete = () => useContext(SocieteContext);

export const SocieteProvider = ({ children }) => {
  const { user, axiosAuth, isTenant, isStaff } = useAuth();
  const [societes, setSocietes] = useState([]);
  const [currentSociete, setCurrentSociete] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSocietes = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    // Rôles portail (client / élève) : pas de contexte société
    if (["client", "eleve", "stagiaire"].includes(user.role)) { setLoading(false); return; }
    // Tenant sans le module "societes" : on ne tente pas l'appel (évite un 403 console)
    const activeModules = user.tenant?.active_modules;
    if (user.tenant_id && Array.isArray(activeModules) && !activeModules.includes("societes")) {
      setLoading(false);
      return;
    }
    try {
      const api = axiosAuth();
      const r = await api.get("/societes");
      const list = r.data || [];
      setSocietes(list);

      // Restore from localStorage or use default.
      // Sentinel "__group__" = explicit "vue groupe" mode chosen by superadmin
      // via SocieteScopeBanner — do NOT auto-persist a default in that case.
      // Iter 342 — la « vue groupe » est réservée à la direction du groupe (superadmin / admin global) ;
      // un utilisateur société est toujours positionné sur une de ses sociétés (une seule à la fois).
      const isGroupUser = (user.role === "superadmin" || user.group_user === true || user.group_access === true) && !user.tenant_id;
      let savedId = localStorage.getItem("blitzz_societe_id");
      if (savedId === "__group__" && !isGroupUser) { savedId = null; localStorage.removeItem("blitzz_societe_id"); }
      if (savedId === "__group__") {
        setCurrentSociete(null);
      } else {
        const saved = list.find(s => s.societe_id === savedId);
        const def = list.find(s => s.is_default) || list[0];
        const active = saved || def || null;
        setCurrentSociete(active);
        // Persist back to localStorage so axiosAuth (in AuthContext) injects the
        // X-Societe-Id header on every subsequent call.
        if (active) {
          localStorage.setItem("blitzz_societe_id", active.societe_id);
        }
      }

      // Holdings : consolidation réservée à la direction du groupe
      const h = isGroupUser ? await api.get("/holdings").catch(() => ({ data: [] })) : { data: [] };
      setHoldings(h.data || []);
    } catch {
      setSocietes([]);
      setCurrentSociete(null);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadSocietes();
  }, [loadSocietes]);

  const switchSociete = useCallback((societe) => {
    if (!societe) return;
    // Persist FIRST so the page reload below reads the correct id.
    localStorage.setItem("blitzz_societe_id", societe.societe_id);
    setCurrentSociete(societe);
    // Hard reload so every page re-fetches with the new X-Societe-Id header
    // (some pages have already cached data from the previous société).
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }, []);

  // Wrap axiosAuth to add X-Societe-Id header
  const societeApi = useCallback(() => {
    const api = axiosAuth();
    if (currentSociete) {
      api.defaults.headers["X-Societe-Id"] = currentSociete.societe_id;
    }
    return api;
  }, [axiosAuth, currentSociete]);

  return (
    <SocieteContext.Provider value={{
      societes,
      currentSociete,
      holdings,
      loading,
      switchSociete,
      societeApi,
      reloadSocietes: loadSocietes,
      hasMutipleSocietes: societes.length > 1,
    }}>
      {children}
    </SocieteContext.Provider>
  );
};
